import { Fragment, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Loading from 'components/loading/Loading'
import NoneAnchor from 'components/NoneAnchor'
import { fetchDashboardInfoKeyword } from 'features/dashboard/info/dashboardInfoSlice'
import { CampaignTypes, OperationStatus, ReviewStatus } from 'features/Codes'
import { equalTo, isPresent } from 'utils'
import Tooltip from 'components/tooltip/Tooltip'
import PropTypes from 'prop-types'
import NullableNumber from 'features/dashboard/table/shared/NullableNumber'
import { showKeywordBiddingStrategyEdit } from 'features/dashboard/operation/keywordBiddingStrategyEdit/keywordBiddingStrategyEditModalSlice'
import { showKeywordLandingUrlEdit } from 'features/dashboard/operation/keywordLandingUrlEdit/keywordLandingUrlEditModalSlice'
import { useAlert } from 'features/dialog/Alert'
import api from 'api/axios/foka'
import { isNil } from 'lodash'

const KeywordDashboardTitleTooltip = ({ status }) => {
  const [first] = status
  return first ? (
    <Tooltip
      position="bottom"
      content={status.map((item, index) => (
        <Fragment key={index}>
          <strong className="tit_tooltip">운영 불가({item.title})</strong>
          <p className="txt_tooltip2">{item.msg}</p>
        </Fragment>
      ))}>
      운영 불가({first.title}){status.length > 1 ? `(${status.length})` : ''}
    </Tooltip>
  ) : null
}
KeywordDashboardTitleTooltip.propTypes = {
  status: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      title: PropTypes.string,
      msg: PropTypes.string,
    })
  ),
}

const KeywordInfo = () => {
  const dispatch = useDispatch()
  const {
    isLoading,
    visible,
    loaded,
    id: keywordId,
    keyword,
  } = useSelector(state => state.dashboard.info)
  const [ApprovingAlert, showApprovingAlert] = useAlert()
  const operationStatus = useMemo(() => {
    if (isNil(keyword.operationStatus)) return []
    return keyword.operationStatus.map(status => OperationStatus[status])
  }, [keyword.operationStatus])
  const handleLandingUrlModify = async () => {
    if (!keyword.deleted) {
      if (
        isPresent(keyword.landingUrl) &&
        keyword.landingUrlReviewStatus === 'APPROVING'
      ) {
        showApprovingAlert()
      } else {
        const {
          data: {
            siteUrl: { pcSiteYn, mobileSiteYn, individualSettingYn },
          },
        } = await api.ad.getBizChannel(keyword.parentDomainInfo.bizChannel.id)
        const siteSetting = {
          hasPcSite: pcSiteYn === 'Y',
          hasMobileSite: mobileSiteYn === 'Y',
          isIndividual: individualSettingYn === 'Y',
        }
        const { id, text, landingUrl } = keyword
        dispatch(
          showKeywordLandingUrlEdit({
            keywords: [
              {
                id,
                text,
                landingUrl,
              },
            ],
            siteSetting,
          })
        )
      }
    }
  }

  useEffect(() => {
    if (visible && !loaded) {
      dispatch(fetchDashboardInfoKeyword({ id: keywordId }))
    }
  }, [dispatch, visible, loaded, keywordId])

  return (
    <>
      <ul className="tab_g6">
        <li>
          <NoneAnchor
            className="link_tab fw_bold"
            style={{ cursor: 'default' }}>
            기본 정보
          </NoneAnchor>
        </li>
      </ul>
      {isLoading ? (
        <div style={{ height: 296 }}>
          <Loading />
        </div>
      ) : (
        <>
          {loaded && (
            <div className="tab_cont">
              <h4 className="screen_out">기본 정보</h4>
              <div className="infosum_dash">
                <table className="tbl_infosum">
                  <caption className="hide_caption">
                    기본 정보 데이터 테이블
                  </caption>
                  <colgroup>
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th>
                        <div className="inner_cell">ID</div>
                      </th>
                      <td>
                        <div className="inner_cell">{keyword.id}</div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        <div className="inner_cell">상태</div>
                      </th>
                      <td>
                        <div className="inner_cell">
                          {operationStatus.find(equalTo(OperationStatus.ON)) ? (
                            <>운영 가능</>
                          ) : (
                            <KeywordDashboardTitleTooltip
                              status={operationStatus}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                    {keyword.keywordType === CampaignTypes.PREMIUM_LINK && (
                      <>
                        <tr>
                          <th>
                            <div className="inner_cell">키워드 입찰가</div>
                          </th>
                          <td>
                            <div className="inner_cell">
                              <NoneAnchor
                                className="link_budget"
                                onClick={() => {
                                  const {
                                    id,
                                    text,
                                    biddingStrategy,
                                    parentDomainInfo: {
                                      adGroup: {
                                        maxBiddingAmount:
                                          adGroupMaxBiddingAmount,
                                      },
                                    },
                                  } = keyword
                                  if (!keyword.deleted) {
                                    dispatch(
                                      showKeywordBiddingStrategyEdit([
                                        {
                                          id,
                                          text,
                                          biddingStrategy,
                                          adGroupMaxBiddingAmount,
                                        },
                                      ])
                                    )
                                  }
                                }}>
                                {!isPresent(
                                  keyword.biddingStrategy.maxBiddingAmount
                                ) && <em className="fc_black">[기본]</em>}
                                <NullableNumber
                                  value={
                                    keyword.biddingStrategy.maxBiddingAmount ||
                                    keyword.parentDomainInfo.adGroup
                                      .maxBiddingAmount
                                  }
                                  suffix={`원`}
                                />
                              </NoneAnchor>
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <th>
                            <div className="inner_cell">랜딩URL</div>
                          </th>
                          <td>
                            <div className="inner_cell">
                              {keyword.landingUrl ? (
                                keyword.landingUrl.individualSettingYn ===
                                'N' ? (
                                  <div className="group_cell">
                                    모바일/PC
                                    <br />
                                    <NoneAnchor
                                      className="link_budget"
                                      onClick={handleLandingUrlModify}>
                                      {keyword.landingUrl.pcLandingUrl
                                        ? `${keyword.landingUrl.pcLandingUrl.protocol.toLowerCase()}://${
                                            keyword.landingUrl.pcLandingUrl.url
                                          }`
                                        : '미설정'}
                                    </NoneAnchor>
                                  </div>
                                ) : (
                                  <>
                                    <div className="group_cell">
                                      모바일
                                      <br />
                                      <NoneAnchor
                                        className="link_budget"
                                        onClick={handleLandingUrlModify}>
                                        {keyword.landingUrl.mobileLandingUrl
                                          ? `${keyword.landingUrl.mobileLandingUrl.protocol.toLowerCase()}://${
                                              keyword.landingUrl
                                                .mobileLandingUrl.url
                                            }`
                                          : '미설정'}
                                      </NoneAnchor>
                                    </div>
                                    <div className="group_cell">
                                      PC
                                      <br />
                                      <NoneAnchor
                                        className="link_budget"
                                        onClick={handleLandingUrlModify}>
                                        {keyword.landingUrl.pcLandingUrl
                                          ? `${keyword.landingUrl.pcLandingUrl.protocol.toLowerCase()}://${
                                              keyword.landingUrl.pcLandingUrl
                                                .url
                                            }`
                                          : '미설정'}
                                      </NoneAnchor>
                                    </div>
                                  </>
                                )
                              ) : (
                                <NoneAnchor
                                  className="link_budget"
                                  onClick={handleLandingUrlModify}>
                                  미설정
                                </NoneAnchor>
                              )}
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
                <ApprovingAlert title="랜딩URL변경">
                  선택된 키워드의 랜딩URL이 현재 {ReviewStatus.APPROVING.title}
                  으로 변경이 불가능합니다.
                </ApprovingAlert>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
export default KeywordInfo
